import type { EquipSlot, Gear, ItemInst, Rarity } from './items.ts';
import type { Job, NpcId, TownId } from './content.ts';

export const HEADS = ['bald', 'tuft', 'antenna', 'hood', 'mohawk'] as const;
export type Head = (typeof HEADS)[number];

export type Look = {
  skin: number;
  cloth: number;
  accent: number;
  head: Head;
  /** Reskin: index into SURVIVORS (src/shared/survivors.ts). Missing on older characters. */
  survivor?: number;
};

export type OriginId = 'knuckle' | 'carapace' | 'lamp' | 'jackleg';

export type SideState = 'new' | 'coach' | 'coach_got' | 'coach_done';

export type QuestState =
  | 'new'
  | 'accepted'
  | 'carrying'
  | 'done'
  | 'hum'
  | 'hum_got'
  | 'hum_done'
  | 'salt'
  | 'salt_got'
  | 'salt_done'
  | 'hiss'
  | 'hiss_got'
  | 'hiss_done'
  | 'drawer'
  | 'drawer_got'
  | 'drawer_done';
export type Pose = 'idle' | 'walk' | 'swing' | 'windup';
export type EnemyKind =
  | 'roach'
  | 'fastroach'
  | 'toad'
  | 'newt'
  | 'slug'
  | 'panback'
  | 'skip'
  | 'filament'
  | 'slick'
  | 'leaker'
  | 'count'
  | 'drifter'
  | 'sleeper'
  | 'mite'
  | 'nail'
  | 'mast'
  | 'tick'
  | 'crab'
  | 'drawer'
  | 'hound'
  | 'walker'
  | 'moth'
  | 'clerk'
  | 'kettle'
  | 'master'
  | 'wanderer'
  | 'foreman'
  | 'blot'
  | 'surveyor'
  | 'mainspring'
  | 'lastnumber';

export type Input = {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  aim: number;
  melee: boolean;
  shoot: boolean;
  interact: boolean;
};

export function emptyInput(): Input {
  return {
    up: false,
    down: false,
    left: false,
    right: false,
    aim: 0,
    melee: false,
    shoot: false,
    interact: false,
  };
}

/** Light, every tick. Pockets travel in InvMsg, only when they change. */
export type YouSnap = {
  id: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  tabs: number;
  meleeCd: number;
  shootCd: number;
  dead: boolean;
  pose: Pose;
  /** Seconds left on the loud-stone blessing, if any. */
  buff: number;
};

export type ActorSnap = {
  id: number;
  kind: 'player' | EnemyKind | 'dog';
  x: number;
  y: number;
  aim: number;
  hp: number;
  maxHp: number;
  name: string;
  pose: Pose;
  look?: Look;
  /** Base id of what is in the hand, for drawing. */
  weapon?: string | null;
  origin?: OriginId;
  dead?: boolean;
  flash?: boolean;
  stun?: boolean;
  /** Loud ones are champions. */
  champ?: boolean;
  boss?: boolean;
  tier?: number;
};

export type ShotStyle = 'bolt' | 'stun' | 'pop' | 'bell' | 'suck';

export type ShotSnap = {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  team: 'player' | 'enemy';
  style: ShotStyle;
  /** What fired it (weapon id or enemy kind); the client picks the projectile art from this. */
  src?: string;
};

export type FxSnap = {
  id: number;
  kind: 'spark' | 'laser' | 'pop' | 'bell' | 'suck' | 'death' | 'loot';
  x: number;
  y: number;
  x0?: number;
  y0?: number;
  r: number;
  /** A boss died here: the client plays the big blast. */
  boss?: boolean;
};

export type DropSnap = {
  id: number;
  x: number;
  y: number;
  /** A stuff id or a gear base id. */
  kind: string;
  n: number;
  rarity?: Rarity;
  name?: string;
  /** Somebody else's for a few more seconds. */
  held?: boolean;
};

export type SnapMsg = {
  t: 'snap';
  tick: number;
  you: YouSnap;
  actors: ActorSnap[];
  shots: ShotSnap[];
  fx: FxSnap[];
  nodes: { id: string; left: number }[];
  drops: DropSnap[];
  fires: { id: number; x: number; y: number; mine: boolean; home: boolean }[];
  /** Everyone on the server, for the maps. */
  others: { id: number; name: string; x: number; y: number }[];
  /** The roaming thing, if it is out. */
  event?: { x: number; y: number; name: string } | null;
};

/** Everything about a person that changes rarely. */
export type InvMsg = {
  t: 'inv';
  name: string;
  origin: OriginId;
  look: Look;
  gear: Gear;
  bag: ItemInst[];
  stacks: Record<string, number>;
  bar: (string | null)[];
  home: boolean;
  quest: QuestState;
  side: SideState;
  marks: string[];
  opened: string[];
  jobs: Job[];
  tally: Tally;
  story: number;
  /** Cache ids with the time (server seconds) they refill for you. */
  looted: Record<string, number>;
  now: number;
};

export type Tally = {
  kills: number;
  louds: number;
  deaths: number;
  jobs: number;
  farthest: number;
  bosses: string[];
};

export function emptyTally(): Tally {
  return { kills: 0, louds: 0, deaths: 0, jobs: 0, farthest: 1, bosses: [] };
}

/** Explored chunks, as base64 bits. Sent when it grows. */
export type MapMsg = { t: 'map'; seen: string };

export type FireMsg = {
  t: 'fire';
  id: number;
  mine: boolean;
  home: boolean;
};

export type TalkMsg = {
  t: 'talk';
  npc: NpcId;
  name: string;
  text: string;
  choices: { id: string; label: string }[];
  shop?: boolean;
  board?: boolean;
};

export type ShopMsg = {
  t: 'shop';
  npc: NpcId;
  name: string;
  /** Plain stock never runs out. The shelf turns over every few minutes. */
  stock: { item: ItemInst; price: number }[];
  shelf: { item: ItemInst; price: number }[];
  stuff: { id: string; price: number }[];
  turns: number;
};

export type BoardMsg = { t: 'board'; npc: NpcId; town: TownId; offers: Job[] };

export type CoachMsg = { t: 'coach'; from: TownId; stops: { id: TownId; name: string; fare: number; known: boolean }[] };

export type StashMsg = {
  t: 'stash';
  gear: ItemInst[];
  stacks: Record<string, number>;
};

export type ClientMsg =
  | {
      t: 'input';
      up?: boolean;
      down?: boolean;
      left?: boolean;
      right?: boolean;
      aim?: number;
      melee?: boolean;
      shoot?: boolean;
      interact?: boolean;
    }
  | { t: 'talk'; npc?: string; choice?: string }
  | { t: 'buy'; uid?: string }
  | { t: 'sell'; uid?: string }
  | { t: 'sellPlain' }
  | { t: 'equip'; uid?: string; slot?: EquipSlot }
  | { t: 'unequip'; slot?: string }
  | { t: 'salvage'; uid?: string }
  | { t: 'use'; id?: string }
  | { t: 'sellStuff'; id?: string; all?: boolean }
  | { t: 'dropGear'; uid?: string }
  | { t: 'dropStuff'; id?: string }
  | { t: 'barSet'; slot?: number; id?: string }
  | { t: 'barUse'; slot?: number }
  | { t: 'buyStuff'; id?: string }
  | { t: 'craft'; id?: string }
  | { t: 'fire'; id?: number; choice?: string }
  | { t: 'stash'; dir?: string; kind?: string; id?: string }
  | { t: 'job'; take?: string; drop?: string; npc?: string }
  | { t: 'coach'; to?: string }
  | { t: 'pick'; id?: number }
  | { t: 'cycle' }
  | { t: 'chat'; text?: string };

export function validUsername(s: string): boolean {
  return /^[a-zA-Z0-9_]{3,16}$/.test(s);
}

export function validPassword(s: string): boolean {
  return s.length >= 6 && s.length <= 72 && !/[\u0000-\u001f]/.test(s);
}

export function validCharName(s: string): boolean {
  const t = s.trim();
  return t.length >= 2 && t.length <= 18 && /^[A-Za-z][A-Za-z0-9 '\-]*$/.test(t);
}
